import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Sun, Shield, Bed, Bath, Square, ShoppingBag, Mountain, GraduationCap, TreePine, TrendingUp, Home, Heart, Car, Plane, Utensils } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
import { SEOHead } from '../components/SEOHead';
import { RelatedCommunities } from '../components/RelatedCommunities';
const placeholderListings = [{
  id: 1,
  price: '$575,000',
  address: '2341 Green Valley Pkwy',
  area: 'Green Valley Ranch',
  beds: 4,
  baths: 3,
  sqft: '2,400',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$485,000',
  address: '891 Warm Springs Rd',
  area: 'Green Valley South',
  beds: 3,
  baths: 2.5,
  sqft: '1,950',
  image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
}, {
  id: 3,
  price: '$850,000',
  address: '9012 Legacy Ct',
  area: 'Green Valley South',
  beds: 4,
  baths: 3,
  sqft: '2,800',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
}];
export function GreenValleyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-564">
      <SEOHead title="Green Valley Real Estate & Homes for Sale | Jenny Gong" description="Discover homes in Green Valley, Henderson's established upscale community known for beautiful parks, top-rated schools, and refined family living." keywords="Green Valley real estate, Green Valley homes for sale, Green Valley Ranch real estate, Henderson luxury homes" canonicalUrl="https://jennygong.com/green-valley" ogImage="https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop" data-id="element-565" />
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-566">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop")'
      }} data-id="element-567">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-568"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-569"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-570">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-571">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-572">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-573">
              Green Valley Homes for Sale
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-574">
              Henderson's beloved established community known for beautiful
              parks, top schools, and refined family living.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white" data-id="element-575">
        <div className="max-w-7xl mx-auto" data-id="element-576">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-577">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-578">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-579">
                Where Families <span className="italic text-gold" data-id="element-580">Thrive</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-581">
                <p data-id="element-582">
                  Green Valley is one of the original master-planned communities
                  in the Las Vegas Valley, established in 1978 by American
                  Nevada Corporation. It was the first community of its kind in
                  Southern Nevada and set the standard for planned development
                  in the region.
                </p>
                <p data-id="element-583">
                  Today, Green Valley encompasses several distinct neighborhoods
                  within{' '}
                  <Link to="/henderson" className="text-gold hover:underline" data-id="element-584">
                    Henderson
                  </Link>
                  , including Green Valley Ranch, Green Valley South, and the
                  areas surrounding the Green Valley Ranch Resort. Nearby{' '}
                  <Link to="/macdonald-highlands" className="text-gold hover:underline" data-id="element-585">
                    MacDonald Highlands
                  </Link>{' '}
                  offers ultra-luxury options for those seeking the pinnacle of
                  Henderson living. With mature landscaping, established
                  infrastructure, and a strong sense of community, it remains
                  one of the most desirable places to live in the valley.
                </p>
                <p data-id="element-586">
                  Homes range from well-maintained $400K properties to luxury
                  estates exceeding $1M in gated sections. The community is
                  particularly popular with families thanks to its exceptional
                  schools, abundant parks, and safe, walkable neighborhoods.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-587">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-588">
                <Heart className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-589" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-590">#1</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-591">
                  Family Community
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-592">
                <GraduationCap className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-593" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-594">Top</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-595">
                  Rated Schools
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-596">
                <TreePine className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-597" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-598">50+</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-599">
                  Parks
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-600">
                <Shield className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-601" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-602">Safe</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-603">
                  Neighborhoods
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The District */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-604">
        <div className="max-w-7xl mx-auto" data-id="element-605">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-606">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-607">
              <div className="relative h-[500px]" data-id="element-608">
                <img src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=2940&auto=format&fit=crop" alt="The District" className="w-full h-full object-cover" data-id="element-609" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-610"></div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-611">
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-612">
                Shopping & Dining
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-613">
                The District at Green Valley Ranch
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-614">
                <p data-id="element-615">
                  The District is Green Valley's premier open-air lifestyle
                  center, featuring upscale shopping, diverse dining, and
                  regular community events. With a charming village-like
                  atmosphere, it's the social hub of the community.
                </p>
                <p data-id="element-616">
                  Residents enjoy boutique shops, popular restaurants like
                  Lucille's Smokehouse BBQ and King's Fish House, a REI flagship
                  store, and seasonal events including outdoor concerts and
                  holiday celebrations.
                </p>
              </div>
              <div className="mt-8 space-y-4" data-id="element-617">
                <div className="flex items-start gap-4" data-id="element-618">
                  <ShoppingBag className="w-6 h-6 text-gold flex-shrink-0 mt-1" data-id="element-619" />
                  <div data-id="element-620">
                    <h4 className="font-serif text-lg text-charcoal" data-id="element-621">
                      Boutique Shopping
                    </h4>
                    <p className="text-charcoal-light font-light text-sm" data-id="element-622">
                      Unique shops and national retailers in a walkable setting.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-623">
                  <Utensils className="w-6 h-6 text-gold flex-shrink-0 mt-1" data-id="element-624" />
                  <div data-id="element-625">
                    <h4 className="font-serif text-lg text-charcoal" data-id="element-626">
                      Diverse Dining
                    </h4>
                    <p className="text-charcoal-light font-light text-sm" data-id="element-627">
                      From casual cafes to upscale restaurants, something for
                      every palate.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Green Valley Ranch Resort */}
      <section className="py-24 px-6 bg-white" data-id="element-628">
        <div className="max-w-7xl mx-auto" data-id="element-629">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-630">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-631">
              Resort Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-632">
              Green Valley Ranch Resort
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-633">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-634">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-635">
                <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2940&auto=format&fit=crop" alt="Resort" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-636" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-637">
                Luxury Resort & Spa
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-638">
                A stunning Station Casinos property featuring a world-class spa,
                multiple pools, and a beautifully landscaped backyard oasis.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-639">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-640">
                <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2940&auto=format&fit=crop" alt="Dining" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-641" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-642">
                Award-Winning Dining
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-643">
                Home to Hank's Fine Steaks, Borracha Mexican Cantina, and
                several other acclaimed restaurants that draw diners from across
                the valley.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-644">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-645">
                <img src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?q=80&w=2940&auto=format&fit=crop" alt="Pool" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-646" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-647">
                The Backyard
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-648">
                An expansive outdoor venue hosting concerts, pool parties, and
                community events throughout the year — a true neighborhood gem.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Things To Do */}
      <section className="py-24 px-6 bg-white" data-id="element-649">
        <div className="max-w-7xl mx-auto" data-id="element-650">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-651">
            <TreePine className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-652" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-653">
              Outdoor Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-654">
              Parks & Recreation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-655">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-656">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-657">
                <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=2940&auto=format&fit=crop" alt="Cornerstone Park" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-658" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-659">
                Cornerstone Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-660">
                A stunning 100-acre park featuring a massive 31-acre lake,
                lighted sports fields, walking trails, and community event
                spaces.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-661">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-662">
                <img src="https://images.unsplash.com/photo-1530288782965-fbad40327e2d?q=80&w=2940&auto=format&fit=crop" alt="Paseo Verde Park sports fields and recreation area in Green Valley Henderson Nevada" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-663" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-664">
                Paseo Verde Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-665">
                A local favorite featuring baseball fields, tennis courts, sand
                volleyball, and extensive picnic areas perfect for family
                gatherings.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-666">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-667">
                <img src="https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?q=80&w=2948&auto=format&fit=crop" alt="Silver Springs Recreation Center" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-668" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-669">
                Silver Springs Recreation Center
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-670">
                A massive community hub offering an outdoor pool, fitness
                center, gymnasium, and dozens of classes for residents of all
                ages.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="group" data-id="element-671">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-672">
                <img src="https://images.unsplash.com/photo-1472214103451-9374bd1c798e?q=80&w=2940&auto=format&fit=crop" alt="Pittman Wash Trail" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-673" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-674">
                Pittman Wash Trail
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-675">
                A scenic 4-mile paved trail running through the heart of Green
                Valley, perfect for cycling, running, and peaceful evening
                walks.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-676">
        <div className="max-w-7xl mx-auto" data-id="element-677">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-678">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-679" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-680">
              Education Excellence
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-681">
              Top-Rated Schools
            </h2>
            <p className="text-charcoal-light font-light max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-682">
              Green Valley is renowned for its exceptional schools, consistently
              ranking among the highest-performing districts in Southern Nevada.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-683">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-684">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-685">
                Elementary & Middle
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-686">
                <li data-id="element-687">• John C. Vanderburg Elementary (Blue Ribbon)</li>
                <li data-id="element-688">• Neil C. Twitchell Elementary</li>
                <li data-id="element-689">• Selma F. Bartlett Elementary</li>
                <li data-id="element-690">• Bob Miller Middle School</li>
                <li data-id="element-691">• Barbara & Hank Greenspun Junior High</li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-692">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-693">
                High Schools
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-694">
                <li data-id="element-695">• Green Valley High School</li>
                <li data-id="element-696">• Coronado High School</li>
                <li data-id="element-697">• Foothill High School</li>
                <li className="pt-4 italic text-xs text-gold" data-id="element-698">
                  Green Valley High is a multi-time recipient of the NIAA Award
                  of Excellence.
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-699">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-700">
                Private & Charter
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-701">
                <li data-id="element-702">• Henderson International School</li>
                <li data-id="element-703">• Coral Academy of Science</li>
                <li data-id="element-704">• Pinecrest Academy</li>
                <li data-id="element-705">• American Heritage Academy</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shopping & Dining */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-706">
        <div className="max-w-7xl mx-auto" data-id="element-707">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-708">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-709">
              <div className="relative h-[600px]" data-id="element-710">
                <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2874&auto=format&fit=crop" alt="Dining" className="w-full h-full object-cover" data-id="element-711" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-712"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-713">
              <ShoppingBag className="w-10 h-10 text-gold mb-6" data-id="element-714" />
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-715">
                Lifestyle & Culinary
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-8" data-id="element-716">
                Shopping & Dining
              </h2>

              <div className="space-y-8" data-id="element-717">
                <div data-id="element-718">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-719">
                    <ShoppingBag className="w-5 h-5 text-gold" data-id="element-720" />
                    The District at Green Valley Ranch
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-721">
                    An upscale open-air lifestyle center featuring boutique
                    shopping, fine dining, and community events in a beautiful
                    pedestrian-friendly setting.
                  </p>
                </div>

                <div data-id="element-722">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-723">
                    <ShoppingBag className="w-5 h-5 text-gold" data-id="element-724" />
                    Galleria at Sunset
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-725">
                    A major regional mall offering over 100 stores, department
                    anchors, and diverse dining options for the whole family.
                  </p>
                </div>

                <div data-id="element-726">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-727">
                    <Utensils className="w-5 h-5 text-gold" data-id="element-728" />
                    Green Valley Parkway Corridor
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-729">
                    Lined with dozens of local eateries, coffee shops, and daily
                    conveniences, making errands and dining out incredibly easy.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare */}
      <section className="py-24 px-6 bg-white" data-id="element-730">
        <div className="max-w-4xl mx-auto text-center" data-id="element-731">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-732">
            <Heart className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-733" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-734">
              Wellness
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-735">
              World-Class Healthcare
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-736">
              Green Valley residents benefit from exceptional medical care. The
              community is anchored by the <strong data-id="element-737">Henderson Hospital</strong>,
              a state-of-the-art facility part of The Valley Health System.
            </p>
            <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-738">
              The area is also served by the{' '}
              <strong data-id="element-739">St. Rose Dominican Hospital - Siena Campus</strong>,
              Dignity Health medical offices, and numerous specialized clinics
              and urgent care centers throughout the neighborhood.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Transportation */}
      <section className="py-24 bg-charcoal px-6 text-white" data-id="element-740">
        <div className="max-w-7xl mx-auto" data-id="element-741">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-742">
            <MapPin className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-743" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-744">
              Connectivity
            </span>
            <h2 className="text-4xl font-serif mb-6" data-id="element-745">
              Transportation & Access
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-746">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-747">
              <Car className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-748" />
              <h4 className="text-xl font-serif mb-2" data-id="element-749">I-215 Beltway</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-750">
                Runs directly through Green Valley, providing rapid transit to
                the airport, the Strip, and the west side.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-751">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-752" />
              <h4 className="text-xl font-serif mb-2" data-id="element-753">US-95</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-754">
                Easily accessible via the Beltway interchange, offering a direct
                route to downtown Las Vegas.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-755">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-756" />
              <h4 className="text-xl font-serif mb-2" data-id="element-757">Harry Reid Airport</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-758">
                Just a 10-15 minute drive from most Green Valley neighborhoods,
                making travel incredibly convenient.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-759">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-760" />
              <h4 className="text-xl font-serif mb-2" data-id="element-761">Major Corridors</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-762">
                Green Valley Pkwy and Valle Verde Dr serve as massive, efficient
                thoroughfares for local transit.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <RelatedCommunities currentCommunity="Green Valley" data-id="element-763" />

      <ListingsComingSoon area="Green Valley" data-id="element-764" />

      <ContactCTA data-id="element-765" />
    </main>;
}